import { ArrowRightIcon, Button, Table, TableColumns } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useAdditionalTeachingLoadsListQuery, useSubjectsListQuery } from '../../../types/planung-graphql-client-defs';
import { sumUpArray } from '../../../utils/arrayFunc';
import { formatNumber } from '../../../utils/helper';
import { useDeputateLessons } from '../../Lessons/hooks/useDeputateLessons';
import { DeputatesTableType } from './PlannedDeputatesTable';

type CurrentDeputatesTableProps = {
  onRowClick: (data: string) => void;
};

export const CurrentDeputatesBySubjectTable: FC<CurrentDeputatesTableProps> = observer(
  ({ onRowClick }: CurrentDeputatesTableProps) => {
    const { t } = useTranslation();
    const { pimAuthClaims } = useAuthClaims();
    const currentSchoolYear = useSelectedSchoolYear();
    const subjectContext = useMemorizedCacheTag('SUBJECTS');
    const atlContext = useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD');

    const { columnVisibility, saveColumnVisibility } = useHiddenColumns('deputate-bysubject-list');
    const { sorting, saveSorting } = useColumnsSort('deputate-bysubject-list');

    const { lessons: lessonData } = useDeputateLessons({
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      schoolYearUuid: currentSchoolYear?.uuid ?? '',
    });

    const [{ data: subjectsData }] = useSubjectsListQuery({
      context: subjectContext,
    });

    const [{ data: addidtionalTeachingLoadData }] = useAdditionalTeachingLoadsListQuery({
      variables: {
        schoolyear: currentSchoolYear?.uuid ?? '',
      },
      context: atlContext,
      pause: !currentSchoolYear,
    });

    const data: DeputatesTableType[] =
      subjectsData?.subjects.map((subject) => {
        const deputateAdditional =
          sumUpArray(
            addidtionalTeachingLoadData?.additionalTeachingLoads
              ?.filter((l) => l.subject?.uuid === subject.uuid)
              .map((a) => a.hours) ?? [],
          ) ?? 0;

        const currentSubjectsLessons = lessonData.filter((l) => l.subjectUuid === subject.uuid);

        return {
          teacherUuid: subject.uuid,
          name: subject.name ?? '',
          teachingBlockWeeks: currentSubjectsLessons.reduce(
            (val, l) => val + (l.teachingLoadInfo?.teachingBlockWeeks ?? 0),
            0,
          ),
          teachingBlocks: currentSubjectsLessons.reduce((val, l) => val + (l.teachingLoadInfo?.teachingBlocks ?? 0), 0),
          deputateAdditional,
          deputateLessonUnit: currentSubjectsLessons.reduce(
            (val, lesson) => val + (lesson.teachingLoadInfo?.fachstundenDeputat ?? 0),
            0,
          ),
          deputateSubjectContainer: currentSubjectsLessons.reduce(
            (val, lesson) => val + (lesson.teachingLoadInfo?.epochenDeputat ?? 0),
            0,
          ),
          contractHours: 0,
          billedHours: 0,
        };
      }) ?? [];

    const columns: TableColumns<DeputatesTableType>[] = [
      {
        header: t('common.name'),
        id: 'name',
        accessorKey: 'name',
        size: 300,
      },
      {
        header: t('common.week', { count: 2 }),
        id: 'weeks',
        accessorKey: 'weeks',
        size: 100,
        alignment: 'right',
      },
      {
        header: t('common.epochWeeks'),
        accessorKey: 'epochWeeks',
        id: 'epochWeeks',
        size: 100,
        alignment: 'right',
      },
      {
        header: t('deputate.deputateEpoch'),
        accessorFn: (row) => formatNumber(row.deputateSubjectContainer),
        id: 'deputateSubjectContainer',
        size: 100,
        alignment: 'right',
      },
      {
        header: t('deputate.deputateSubject'),
        accessorFn: (row) => formatNumber(row.deputateLessonUnit),
        id: 'deputateLessonUnit',
        size: 100,
        alignment: 'right',
      },
      {
        header: t('deputate.deputateManually'),
        accessorFn: (row) => formatNumber(row.deputateAdditional),
        id: 'deputateAdditional',
        size: 100,
        alignment: 'right',
      },
      {
        header: t('common.sum'),
        accessorKey: 'sum',
        accessorFn: (row) => {
          return formatNumber(row.deputateAdditional + row.deputateSubjectContainer + row.deputateLessonUnit);
        },
        size: 100,
        id: 'sum',
        alignment: 'right',
      },
    ];

    return (
      <Table<DeputatesTableType>
        showBorderRadius
        showRowHover
        showShadow
        canScroll
        minHeight={1000}
        columns={columns}
        data={data}
        sorting={sorting}
        onSortingChange={saveSorting}
        isOnWhite={false}
        showVisibility
        showActionBar
        actionBarSettings={{
          showPrintButton: true,
        }}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={saveColumnVisibility}
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('subject.title', { count: 2 })} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
          filename: `${t('subject.title', { count: 2 })}_${currentSchoolYear?.shortName}`,
        }}
        showSelect={false}
        onRowClick={(e, row) => {
          onRowClick(row.original.teacherUuid);
        }}
        lastColWidth='60px'
        lastCol={(row) => {
          return <ArrowRightIcon className='small' />;
        }}
      />
    );
  },
);
